import React, { useMemo, useState } from "react";
import {
  MdAdd,
  MdChevronRight,
  MdEdit,
  MdExpandMore,
  MdUnfoldLess,
  MdUnfoldMore,
} from "react-icons/md";
import {
  ProjectTask,
  TaskType,
} from "../../../../interfaces/ProjectTask";

interface Props {
  tasks: ProjectTask[];
  onEdit: (task: ProjectTask) => void;
  /** Abre o modal de criação com tipo e pai pré-definidos. */
  onAdd: (type: number, parentId: number | null) => void;
  /** Marca/desmarca conclusão (status Done). Concluir o pai conclui os filhos (cascata no backend). */
  onToggleComplete: (task: ProjectTask) => void;
  /** Habilita "Novo épico" (precisa de um projeto filtrado). */
  canAdd: boolean;
  /**
   * Re-vincula a tarefa arrastada a um novo pai (`null` = solta na raiz). A profundidade é livre;
   * o servidor recusa apenas ciclo, e ajusta o tipo ao nível do destino.
   */
  onReparent?: (task: ProjectTask, newParentId: number | null) => void;
  /** Horas realizadas (timesheet) por tarefa, para estimado × realizado. */
  workedByTask?: Record<number, number>;
}

const typeChip: Record<number, { label: string; cls: string }> = {
  [TaskType.Epic]: { label: "Épico", cls: "bg-dv-magenta-500/10 text-dv-magenta-700 dark:bg-dv-magenta-500/15 dark:text-dv-magenta-300" },
  [TaskType.Task]: { label: "Tarefa", cls: "bg-dv-blue-500/10 text-dv-blue-700 dark:bg-dv-blue-500/15 dark:text-dv-blue-300" },
  [TaskType.Subtask]: { label: "Subtarefa", cls: "bg-gray-100 text-gray-600 dark:bg-navy-900 dark:text-gray-300" },
};

const isDone = (t: ProjectTask) => t.statusCategory === "Done";

const assigneeLabel = (t: ProjectTask) =>
  t.assignees && t.assignees.length > 0
    ? t.assignees.map((a) => a.name).filter(Boolean).join(", ")
    : t.assigneeName ?? "Sem responsável";

const TaskHierarchy: React.FC<Props> = ({ tasks, onEdit, onAdd, onToggleComplete, canAdd, workedByTask, onReparent }) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const { topLevel, childrenOf } = useMemo(() => {
    const childrenOf = new Map<number, ProjectTask[]>();
    const top: ProjectTask[] = [];
    tasks.forEach((t) => {
      if (t.parentTaskId == null) {
        // Épicos e tarefas sem épico ficam no topo.
        if (t.type === TaskType.Epic || t.type === TaskType.Task) top.push(t);
      } else {
        const arr = childrenOf.get(t.parentTaskId) ?? [];
        arr.push(t);
        childrenOf.set(t.parentTaskId, arr);
      }
    });
    const order = (a: ProjectTask, b: ProjectTask) => a.orderIndex - b.orderIndex || a.id - b.id;
    top.sort(order);
    childrenOf.forEach((arr) => arr.sort(order));
    return { topLevel: top, childrenOf };
  }, [tasks]);

  // Realizado acumulado por tarefa = horas próprias (timesheet) + soma das descendentes.
  const realizedById = useMemo(() => {
    const worked = workedByTask ?? {};
    const m = new Map<number, number>();
    const calc = (id: number): number => {
      if (m.has(id)) return m.get(id)!;
      const own = worked[id] ?? 0;
      const kids = childrenOf.get(id) ?? [];
      const sum = Math.round((own + kids.reduce((a, k) => a + calc(k.id), 0)) * 100) / 100;
      m.set(id, sum);
      return sum;
    };
    tasks.forEach((t) => calc(t.id));
    return m;
  }, [tasks, childrenOf, workedByTask]);

  const toggle = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Recolher/expandir tudo: só nós com filhos entram no set; estado misto conta como "expandido".
  const parentIds = useMemo(() => Array.from(childrenOf.keys()), [childrenOf]);
  const allCollapsed = parentIds.length > 0 && parentIds.every((id) => collapsed.has(id));
  const toggleAll = () => setCollapsed(allCollapsed ? new Set() : new Set(parentIds));

  // ---- Arrastar para re-vincular -------------------------------------------------------------
  // HTML5 nativo em vez do @hello-pangea/dnd que o Kanban usa: aquela biblioteca resolve REORDENAR
  // uma lista, e aqui o gesto é outro — soltar um item DENTRO de outro. Árvore com droppable
  // aninhado é onde ela dói mais, e o ganho não existe: o alvo aqui é a linha inteira.
  const [dragId, setDragId] = useState<number | null>(null);
  const [overId, setOverId] = useState<number | "root" | null>(null);

  // Descendentes do item arrastado: soltar dentro deles criaria ciclo, e o roll-up de estimativa
  // sobe a cadeia de pais — ciclo ali pendura o request. O servidor recusa; a tela nem oferece.
  const dragDescendants = useMemo(() => {
    const out = new Set<number>();
    if (dragId == null) return out;
    let frontier = [dragId];
    while (frontier.length) {
      const next: number[] = [];
      for (const id of frontier)
        for (const k of childrenOf.get(id) ?? [])
          if (!out.has(k.id)) { out.add(k.id); next.push(k.id); }
      frontier = next;
    }
    return out;
  }, [dragId, childrenOf]);

  const canDropOn = (targetId: number | "root") => {
    if (dragId == null || !onReparent) return false;
    if (targetId === "root") return true;
    return targetId !== dragId && !dragDescendants.has(targetId);
  };

  const drop = (targetId: number | "root") => {
    const arrastada = tasks.find((t) => t.id === dragId);
    setOverId(null);
    setDragId(null);
    if (!arrastada || !onReparent || !canDropOn(targetId)) return;
    const novoPai = targetId === "root" ? null : targetId;
    if ((arrastada.parentTaskId ?? null) === novoPai) return; // já está lá
    onReparent(arrastada, novoPai);
  };

  const Row: React.FC<{ task: ProjectTask; depth: number }> = ({ task, depth }) => {
    const kids = childrenOf.get(task.id) ?? [];
    const hasKids = kids.length > 0;
    const open = !collapsed.has(task.id);
    const chip = typeChip[task.type ?? TaskType.Task];
    const total = task.subtaskCount ?? 0;
    const done = task.completedSubtaskCount ?? 0;
    const est = task.estimatedHours ?? 0;
    const real = realizedById.get(task.id) ?? 0;

    return (
      <>
        <div
          draggable={!!onReparent}
          onDragStart={(e) => {
            setDragId(task.id);
            e.dataTransfer.effectAllowed = "move";
            // Alguns navegadores não iniciam o arrasto sem payload.
            e.dataTransfer.setData("text/plain", String(task.id));
          }}
          onDragEnd={() => { setDragId(null); setOverId(null); }}
          onDragOver={(e) => {
            if (!canDropOn(task.id)) return;
            e.preventDefault(); // sem isto o navegador recusa o drop
            e.dataTransfer.dropEffect = "move";
            if (overId !== task.id) setOverId(task.id);
          }}
          onDragLeave={() => setOverId((o) => (o === task.id ? null : o))}
          onDrop={(e) => { e.preventDefault(); drop(task.id); }}
          className={`flex items-center gap-2 border-b border-gray-100 py-2 pr-2 transition-colors last:border-b-0 dark:border-white/5 ${
            overId === task.id && canDropOn(task.id)
              ? "bg-dv-blue-500/10 ring-1 ring-inset ring-dv-blue-500/40 dark:bg-dv-blue-500/15"
              : dragId === task.id
              ? "opacity-40"
              : "hover:bg-gray-50 dark:hover:bg-navy-700/40"
          } ${onReparent ? "cursor-grab active:cursor-grabbing" : ""}`}
          style={{ paddingLeft: depth * 22 + 8 }}
          title={onReparent ? "Arraste para dentro de outra tarefa para vincular" : undefined}
        >
          <button
            type="button"
            onClick={() => hasKids && toggle(task.id)}
            aria-label={open ? "Recolher" : "Expandir"}
            className={`flex h-5 w-5 items-center justify-center text-gray-400 ${hasKids ? "hover:text-gray-700 dark:hover:text-gray-200" : "invisible"}`}
            title={open ? "Recolher" : "Expandir"}
          >
            {open ? <MdExpandMore className="h-[18px] w-[18px]" aria-hidden="true" /> : <MdChevronRight className="h-[18px] w-[18px]" aria-hidden="true" />}
          </button>

          <input
            type="checkbox"
            checked={isDone(task)}
            onChange={() => onToggleComplete(task)}
            className="h-4 w-4 cursor-pointer accent-dv-blue-500"
            title={isDone(task) ? "Concluído" : "Marcar como concluído"}
          />

          <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${chip.cls}`}>
            {chip.label}
          </span>

          <span
            className={`min-w-0 flex-1 truncate text-sm ${
              isDone(task)
                ? "text-gray-500 line-through dark:text-gray-500"
                : "font-medium text-navy-700 dark:text-white"
            }`}
          >
            {task.title}
          </span>

          {total > 0 && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-gray-700 dark:bg-navy-900 dark:text-gray-300">
              {done}/{total}
            </span>
          )}

          {(est > 0 || real > 0) && (
            <span
              className="hidden shrink-0 items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700 md:inline-flex dark:bg-navy-900 dark:text-gray-300"
              title="Estimado / Realizado (horas)"
            >
              est {est}h
              <span className="text-gray-400" aria-hidden="true">·</span>
              <span className={est > 0 && real > est ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}>
                real {real}h
              </span>
            </span>
          )}

          <span
            className="hidden w-36 shrink-0 truncate text-[11px] text-gray-700 sm:block dark:text-gray-400"
            title={assigneeLabel(task)}
          >
            {assigneeLabel(task)}
          </span>

          {task.type === TaskType.Epic && (
            <button type="button" onClick={() => onAdd(TaskType.Task, task.id)} aria-label="Adicionar tarefa" className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:hover:bg-navy-700" title="Adicionar tarefa">
              <MdAdd className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {task.type === TaskType.Task && (
            <button type="button" onClick={() => onAdd(TaskType.Subtask, task.id)} aria-label="Adicionar subtarefa" className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:hover:bg-navy-700" title="Adicionar subtarefa">
              <MdAdd className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button type="button" onClick={() => onEdit(task)} aria-label="Editar tarefa" className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:hover:bg-navy-700" title="Editar">
            <MdEdit className="h-[15px] w-[15px]" aria-hidden="true" />
          </button>
        </div>

        {open && kids.map((k) => <Row key={k.id} task={k} depth={depth + 1} />)}
      </>
    );
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-2xl text-xs text-gray-700 dark:text-gray-400">
          Hierarquia Épico → Tarefa → Subtarefa. <span className="font-semibold text-navy-700 dark:text-gray-200">est</span> = estimado,
          {" "}<span className="font-semibold text-navy-700 dark:text-gray-200">real</span> = horas apontadas (timesheet), acumulado por nível.
          Marque o checkbox para concluir (concluir o pai conclui os filhos).
        </p>
        <div className="flex shrink-0 items-center gap-2.5">
          <button
            type="button"
            onClick={toggleAll}
            disabled={parentIds.length === 0}
            title={allCollapsed ? "Expandir todos os itens da árvore" : "Recolher todos os itens da árvore"}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            {allCollapsed ? <MdUnfoldMore className="h-4 w-4" aria-hidden="true" /> : <MdUnfoldLess className="h-4 w-4" aria-hidden="true" />}
            {allCollapsed ? "Expandir tudo" : "Recolher tudo"}
          </button>
          <button
            type="button"
            onClick={() => onAdd(TaskType.Epic, null)}
            disabled={!canAdd}
            title={canAdd ? "" : "Selecione um projeto para criar um épico"}
            className="flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdAdd className="h-4 w-4" aria-hidden="true" /> Novo épico
          </button>
        </div>
      </div>

      {topLevel.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 py-12 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          Nenhum item ainda. Crie um épico e adicione tarefas e subtarefas dentro dele.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-white/10">
          {/*
            Zona de raiz: aparece só durante o arrasto. Sem ela, dá para vincular e não dá para
            DESvincular — o gesto teria volta apenas pelo modal, e a árvore viraria um caminho de
            mão única.
          */}
          {dragId != null && (
            <div
              onDragOver={(e) => { e.preventDefault(); setOverId("root"); }}
              onDragLeave={() => setOverId((o) => (o === "root" ? null : o))}
              onDrop={(e) => { e.preventDefault(); drop("root"); }}
              className={`border-b border-dashed py-2 text-center text-xs font-semibold transition-colors ${
                overId === "root"
                  ? "border-dv-blue-500/40 bg-dv-blue-500/10 text-dv-blue-700 dark:text-dv-blue-300"
                  : "border-gray-200 text-gray-500 dark:border-white/10 dark:text-gray-400"
              }`}
            >
              Solte aqui para desvincular (virar item de topo)
            </div>
          )}
          {topLevel.map((t) => (
            <Row key={t.id} task={t} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskHierarchy;
